import { castArray, pickStrict } from 'vtils'
import { onActivated, onMounted, reactive, watch } from '@vue/composition-api'
import { OneOrMore } from 'vtils/types'
import { prepareData } from '@/utils'
import { useActivated } from './use_activated'

export function useApi<T>(
  service: () => Promise<T>,
  options?: {
    refreshOnActivated?: boolean
    refreshOnChange?: () => any
    onError?: (err: any) => any
  },
) {
  const state: {
    fetching: boolean
    loading: boolean
    data: T
    refresh: () => Promise<void>
  } = reactive({
    fetching: true,
    loading: true,
    data: {} as any,
    refresh: () => {
      state.fetching = true
      return service().then(res => {
        state.data = res
        state.fetching = false
        state.loading = false
      })
    },
  })
  const run = async () => {
    try {
      state.fetching = true
      const res = await service()
      state.data = res
      state.fetching = false
      state.loading = false
    } catch (err) {
      options?.onError?.(err)
      throw err
    }
  }

  if (options?.refreshOnActivated) {
    onActivated(run)
  } else {
    onMounted(run)
  }

  if (options?.refreshOnChange) {
    watch(options.refreshOnChange, run)
  }

  return state
}

export function useApiPlus<T extends Record<string, () => any>>(
  service: T,
  options?: {
    refreshOnActivated?: boolean
    refreshOnChange?: () => any
    onError?: (err: any) => any
    onChange?: (payload: {
      data: {
        [K in keyof T]: T[K] extends () => Promise<infer X>
          ? X
          : Awaited<ReturnType<T[K]>>
      }
      first: boolean
    }) => any
  },
) {
  const state: {
    fetching: boolean
    loading: boolean
    data: {
      [K in keyof T]: T[K] extends () => Promise<infer X>
        ? X
        : Awaited<ReturnType<T[K]>>
    }
    refresh: (keys?: OneOrMore<keyof T>) => Promise<void>
  } = reactive({
    fetching: true,
    loading: true,
    data: {} as any,
    refresh: keys => {
      state.fetching = true
      return prepareData(
        !keys ? service : pickStrict(service, castArray(keys)),
      ).then(async res => {
        state.data = {
          ...state.data,
          ...res,
        }
        await options?.onChange?.({ data: state.data, first: false })
        state.fetching = false
        state.loading = false
      })
    },
  })
  const run = async () => {
    try {
      state.fetching = true
      const res = await prepareData(service)
      state.data = res as any
      await options?.onChange?.({ data: state.data, first: true })
      state.fetching = false
      state.loading = false
    } catch (err) {
      options?.onError?.(err)
      throw err
    }
  }

  if (options?.refreshOnActivated) {
    useActivated(() => run())
  } else {
    onMounted(run)
  }

  if (options?.refreshOnChange) {
    watch(options.refreshOnChange, run)
  }

  return state
}
